import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Plus, X, Trash, PencilSimple, FloppyDisk, DownloadSimple, FunnelSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCY_TONE = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-amber-50 text-amber-700 border-amber-300",
  medium: "bg-blue-50 text-blue-700 border-blue-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_TONE = {
  mandatory: "bg-red-50 text-red-700 border-red-200",
  preferred: "bg-blue-50 text-blue-700 border-blue-200",
};

const SORT_LABEL = {
  urgency: "Urgency",
  market_reach: "Market reach",
  cost: "Cost",
  duration: "Duration",
  expected_readiness_improvement: "Readiness improvement",
};

function Chip({ tone, children, testId }) {
  return <span data-testid={testId} className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${tone}`}>{children}</span>;
}

function BriefViewer({ brief, onClose }) {
  const download = () => {
    const blob = new Blob([JSON.stringify(brief, null, 2)],
      { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `project-brief-${brief.recommendation_id}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div onClick={(e) => e.stopPropagation()} className="w-[640px] max-w-full bg-white border-l border-slate-200 overflow-y-auto p-5 space-y-4" data-testid="brief-viewer">
        <header className="flex items-start justify-between">
          <div>
            <p className="overline-label text-slate-400">Project brief</p>
            <p className="font-heading font-bold text-sm mt-0.5">{brief.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="brief-download" onClick={download}
              className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-2.5 py-1.5 hover:bg-slate-50 flex items-center gap-1"><DownloadSimple size={11} /> Export JSON</button>
            <button data-testid="brief-close" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1"><X size={16} /></button>
          </div>
        </header>
        <div className="flex flex-wrap gap-2">
          <Chip tone={URGENCY_TONE[brief.meta.urgency]}>{brief.meta.urgency}</Chip>
          <Chip tone={STATUS_TONE[brief.meta.mandatory_or_preferred]}>{brief.meta.mandatory_or_preferred}</Chip>
          <Chip tone="bg-slate-100 text-slate-700 border-slate-200">{brief.meta.project_status}</Chip>
          {brief.meta.expected_readiness_improvement !== null && (
            <Chip tone="bg-emerald-50 text-emerald-700 border-emerald-200">
              +{brief.meta.expected_readiness_improvement} readiness pts
            </Chip>
          )}
        </div>

        {[
          ["Situation", brief.situation, [
            ["Current evidence", brief.situation.current_evidence],
            ["Exact gap", brief.situation.exact_gap],
            ["Why it matters", brief.situation.why_it_matters],
            ["Affected markets", brief.situation.affected_markets.map((m) => m.label).join(", ") || "—"],
            ["Closest reusable asset", brief.situation.closest_reusable_asset],
          ]],
          ["Approach", brief.approach, [
            ["Required method", brief.approach.required_method],
            ["Required data", brief.approach.required_data],
            ["Deliverables", brief.approach.deliverables.join(" · ")],
            ["Dependencies", brief.approach.dependencies.join(" · ")],
          ]],
          ["Plan", brief.plan, [
            ["Owner", brief.plan.owner],
            ["Duration (weeks)", brief.plan.duration_weeks],
            ["Cost range",
              (brief.plan.cost_range.low !== null && brief.plan.cost_range.high !== null)
                ? `${brief.plan.cost_range.low.toLocaleString()} – ${brief.plan.cost_range.high.toLocaleString()} ${brief.plan.cost_range.currency}`
                : "—"],
          ]],
          ["Risk", brief.risk, [
            ["Remaining uncertainty", brief.risk.remaining_uncertainty],
            ["Consequence of no action", brief.risk.consequence_of_no_action],
          ]],
        ].map(([label, _, rows]) => (
          <section key={label}>
            <p className="overline-label text-slate-400">{label}</p>
            <table className="w-full text-xs mt-1">
              <tbody>
                {rows.map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-100">
                    <td className="py-1.5 pr-3 text-slate-500 w-1/3">{k}</td>
                    <td className="py-1.5 font-semibold text-slate-800 whitespace-pre-wrap">{v || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

        <p className="text-[10px] text-slate-400">Generated {new Date(brief.generated_at).toLocaleString()}</p>
      </div>
    </div>
  );
}

function RecForm({ meta, initial, onCancel, onSaved, countries, productId }) {
  const [f, setF] = useState(initial || {
    title: "", urgency: "high", mandatory_or_preferred: "preferred",
    current_evidence: "", exact_gap: "", why_it_matters: "",
    affected_markets: [], closest_reusable_asset: "",
    required_method: "", required_data: "",
    deliverables: "", owner: "", dependencies: "",
    duration_weeks: "", cost_low: "", cost_high: "", cost_currency: "USD",
    expected_readiness_improvement: "", confidence: "medium",
    remaining_uncertainty: "", consequence_of_no_action: "",
    project_status: "proposed",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.title.trim().length < 5) return toast.error("Title requires 5+ chars");
    setBusy(true);
    try {
      const body = {
        ...f,
        product_id: productId,
        deliverables: typeof f.deliverables === "string" ? f.deliverables.split(/\n|;/).map((s) => s.trim()).filter(Boolean) : f.deliverables,
        dependencies: typeof f.dependencies === "string" ? f.dependencies.split(/\n|;/).map((s) => s.trim()).filter(Boolean) : f.dependencies,
        duration_weeks: f.duration_weeks === "" ? null : Number(f.duration_weeks),
        cost_low:      f.cost_low === "" ? null : Number(f.cost_low),
        cost_high:     f.cost_high === "" ? null : Number(f.cost_high),
        expected_readiness_improvement: f.expected_readiness_improvement === "" ? null : Number(f.expected_readiness_improvement),
      };
      if (initial?.id) {
        await axios.patch(`${API}/recommendations/${initial.id}`, body);
        toast.success("Recommendation updated");
      } else {
        await axios.post(`${API}/recommendations`, body);
        toast.success("Recommendation created");
      }
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? (detail[0]?.msg || JSON.stringify(detail))
        : "Save failed";
      toast.error(msg);
    }
    finally { setBusy(false); }
  };
  const toggleMarket = (id) => setF((v) => ({
    ...v,
    affected_markets: v.affected_markets.includes(id)
      ? v.affected_markets.filter((m) => m !== id)
      : [...v.affected_markets, id],
  }));
  return (
    <div className="bg-white border border-slate-200 p-5 space-y-3" data-testid="rec-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Select value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}>
          <SelectTrigger data-testid="rec-urgency" className="h-8 text-xs bg-white"><SelectValue placeholder="Urgency" /></SelectTrigger>
          <SelectContent className="bg-white">{meta.urgency.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.mandatory_or_preferred} onValueChange={(v) => setF({ ...f, mandatory_or_preferred: v })}>
          <SelectTrigger data-testid="rec-mand" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{meta.status.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.confidence} onValueChange={(v) => setF({ ...f, confidence: v })}>
          <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{meta.confidence.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.project_status} onValueChange={(v) => setF({ ...f, project_status: v })}>
          <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{meta.project_status.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Input data-testid="rec-title" className="h-8 text-xs" placeholder="Recommendation title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Textarea data-testid="rec-current" rows={2} className="text-xs" placeholder="Current evidence" value={f.current_evidence} onChange={(e) => setF({ ...f, current_evidence: e.target.value })} />
        <Textarea data-testid="rec-gap" rows={2} className="text-xs" placeholder="Exact gap" value={f.exact_gap} onChange={(e) => setF({ ...f, exact_gap: e.target.value })} />
        <Textarea data-testid="rec-why" rows={2} className="text-xs" placeholder="Why it matters" value={f.why_it_matters} onChange={(e) => setF({ ...f, why_it_matters: e.target.value })} />
      </div>
      <div>
        <p className="overline-label text-slate-400">Affected markets</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {countries.map((c) => (
            <button key={c.id} data-testid={`rec-market-${c.id}`} type="button" onClick={() => toggleMarket(c.id)}
              className={`text-[10px] font-semibold px-2 py-1 border ${f.affected_markets.includes(c.id) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {c.flag} {c.name}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input className="h-8 text-xs" placeholder="Closest reusable asset" value={f.closest_reusable_asset} onChange={(e) => setF({ ...f, closest_reusable_asset: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Required method" value={f.required_method} onChange={(e) => setF({ ...f, required_method: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Required data" value={f.required_data} onChange={(e) => setF({ ...f, required_data: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Textarea rows={2} className="text-xs" placeholder="Deliverables (one per line)" value={f.deliverables} onChange={(e) => setF({ ...f, deliverables: e.target.value })} />
        <Textarea rows={2} className="text-xs" placeholder="Dependencies (one per line)" value={f.dependencies} onChange={(e) => setF({ ...f, dependencies: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Input className="h-8 text-xs" placeholder="Owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Duration (wks)" value={f.duration_weeks} onChange={(e) => setF({ ...f, duration_weeks: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Cost low" value={f.cost_low} onChange={(e) => setF({ ...f, cost_low: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Cost high" value={f.cost_high} onChange={(e) => setF({ ...f, cost_high: e.target.value })} />
        <Input className="h-8 text-xs" placeholder="Currency" value={f.cost_currency} onChange={(e) => setF({ ...f, cost_currency: e.target.value })} />
        <Input data-testid="rec-improvement" className="h-8 text-xs" placeholder="Readiness Δ (pts)" value={f.expected_readiness_improvement} onChange={(e) => setF({ ...f, expected_readiness_improvement: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Textarea rows={2} className="text-xs" placeholder="Remaining uncertainty" value={f.remaining_uncertainty} onChange={(e) => setF({ ...f, remaining_uncertainty: e.target.value })} />
        <Textarea rows={2} className="text-xs" placeholder="Consequence of no action" value={f.consequence_of_no_action} onChange={(e) => setF({ ...f, consequence_of_no_action: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">Cancel</button>
        <button data-testid="rec-save" onClick={submit} disabled={busy}
          className="text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <CircleNotch size={11} className="animate-spin" /> : <FloppyDisk size={11} />} {initial?.id ? "Save changes" : "Create recommendation"}
        </button>
      </div>
    </div>
  );
}

export default function PlannerPage() {
  const { productId, product } = useProduct();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("urgency");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [brief, setBrief] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    axios.get(`${API}/recommendations/meta`).then((r) => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/intelligence/${productId}`).then((r) => setCountries(r.data?.countries || [])).catch(() => setCountries([]));
  }, [productId]);

  const load = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    axios.get(`${API}/recommendations/portfolio`, { params: { product_id: productId, sort } })
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error("Load failed"))
      .finally(() => setLoading(false));
  }, [productId, sort]);
  useEffect(() => { load(); }, [load]);

  const openBrief = async (rec) => {
    try {
      const r = await axios.get(`${API}/recommendations/${rec.id}/brief`);
      setBrief(r.data);
    } catch { toast.error("Brief fetch failed"); }
  };

  const del = async (rec) => {
    if (!window.confirm("Delete this recommendation?")) return;
    try {
      await axios.delete(`${API}/recommendations/${rec.id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <PageShell pageId="planner" overline={`Recommendation Planner · ${product?.name || ""}`}
      title="Detailed Recommendation Planner">
      <div className="flex flex-wrap items-center gap-2">
        <span className="overline-label text-slate-400 flex items-center gap-1"><FunnelSimple size={11} /> Sort by</span>
        {Object.entries(SORT_LABEL).map(([k, l]) => (
          <button key={k} data-testid={`sort-${k}`} onClick={() => setSort(k)}
            className={`text-[10px] font-semibold px-2.5 py-1 border ${sort === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
        <button data-testid="new-rec-btn" onClick={() => { setEditing(null); setCreating((x) => !x); }}
          className="ml-auto text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 flex items-center gap-1.5">
          <Plus size={11} /> {creating ? "Close form" : "New recommendation"}
        </button>
      </div>

      {creating && meta && (
        <RecForm meta={meta} countries={countries} productId={productId}
          initial={editing || undefined}
          onCancel={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}

      {loading ? (
        <div data-testid="planner-loading" className="py-16 text-center text-xs text-slate-400">Loading portfolio…</div>
      ) : rows.length === 0 ? (
        <div data-testid="planner-empty" className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-200">
          No recommendations yet for {product?.name || "this product"} — create one to start turning gaps into projects.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs" data-testid="planner-table">
            <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Urgency</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Markets</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Duration (wk)</th>
                <th className="text-left px-3 py-2">Cost</th>
                <th className="text-left px-3 py-2">Δ readiness</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`rec-row-${r.id}`} className="hover:bg-slate-50/40">
                  <td className="px-3 py-2 font-semibold text-slate-800 max-w-[280px]">
                    <div className="truncate">{r.title}</div>
                    {r.owner && <div className="text-[10px] text-slate-400 truncate">Owner: {r.owner}</div>}
                  </td>
                  <td className="px-3 py-2"><Chip tone={URGENCY_TONE[r.urgency]}>{r.urgency}</Chip></td>
                  <td className="px-3 py-2"><Chip tone={STATUS_TONE[r.mandatory_or_preferred]}>{r.mandatory_or_preferred}</Chip></td>
                  <td className="px-3 py-2 text-slate-500 text-[10px]">{(r.affected_markets || []).length ? r.affected_markets.join(", ") : "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.owner || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.duration_weeks ?? "—"}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono-data">{r.cost_low !== null ? `${r.cost_low.toLocaleString()}–${r.cost_high.toLocaleString()} ${r.cost_currency}` : "—"}</td>
                  <td className="px-3 py-2 font-semibold">{r.expected_readiness_improvement !== null ? `+${r.expected_readiness_improvement}` : "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                    <button data-testid={`rec-brief-${r.id}`} onClick={() => openBrief(r)}
                      className="text-[10px] font-semibold text-slate-700 border border-slate-300 px-2 py-1 hover:bg-slate-50">Brief</button>
                    <button data-testid={`rec-edit-${r.id}`} onClick={() => { setEditing(r); setCreating(true); }}
                      className="text-slate-300 hover:text-slate-900 p-1"><PencilSimple size={12} /></button>
                    <button data-testid={`rec-del-${r.id}`} onClick={() => del(r)}
                      className="text-slate-300 hover:text-red-600 p-1"><Trash size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {brief && <BriefViewer brief={brief} onClose={() => setBrief(null)} />}
    </PageShell>
  );
}
